import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { useLang } from "@/context/LangContext";
import { TrendingUp, DollarSign, Wrench, ShieldAlert, Sparkles, ArrowRight } from "lucide-react";
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
  AreaChart, Area, PieChart, Pie, Cell,
} from "recharts";
import Heatmap from "@/components/Heatmap";
import DashboardAlerts from "@/components/DashboardAlerts";
import LedgerKPICards from "@/components/LedgerKPICards";
import { PIPELINE_COLUMNS } from "@/components/KanbanBoard";

const ACCENT_BG = {
  slate: "bg-slate-800/40 border-slate-700/60 text-slate-300",
  amber: "bg-amber-500/10 border-amber-500/30 text-amber-300",
  orange: "bg-orange-500/10 border-orange-500/30 text-orange-300",
  blue: "bg-blue-500/10 border-blue-500/30 text-blue-300",
  cyan: "bg-cyan-500/10 border-cyan-500/30 text-cyan-300",
  indigo: "bg-indigo-500/10 border-indigo-500/30 text-indigo-300",
  purple: "bg-purple-500/10 border-purple-500/30 text-purple-300",
  teal: "bg-teal-500/10 border-teal-500/30 text-teal-300",
  emerald: "bg-emerald-500/10 border-emerald-500/30 text-emerald-300",
};

const KPI = ({ label, value, sub, icon: Icon, accent = "blue" }) => (
  <div
    data-testid={`kpi-${label.replace(/\s/g, "-").toLowerCase()}`}
    className="relative rounded-xl border border-slate-800 bg-[#0B1120] p-6 hover:border-blue-500/40 transition-colors group"
  >
    <div className="flex items-start justify-between">
      <div className="text-[10px] uppercase tracking-[0.22em] font-bold text-slate-500">{label}</div>
      <div className={`w-8 h-8 rounded-lg flex items-center justify-center ${accent === "blue" ? "bg-blue-500/10 text-blue-400" : accent === "emerald" ? "bg-emerald-500/10 text-emerald-400" : accent === "amber" ? "bg-amber-500/10 text-amber-400" : "bg-cyan-500/10 text-cyan-400"}`}>
        <Icon className="w-4 h-4" />
      </div>
    </div>
    <div className="font-display text-3xl font-semibold mt-3 tracking-tight">{value}</div>
    {sub && <div className="text-xs text-slate-500 mt-1.5">{sub}</div>}
  </div>
);

const PIE_COLORS = ["#2563EB", "#10B981", "#F59E0B", "#06B6D4", "#A855F7", "#EF4444"];

export default function Dashboard() {
  const { t } = useLang();
  const [kpis, setKpis] = useState(null);
  const [heatmap, setHeatmap] = useState(null);
  const [pipeline, setPipeline] = useState(null);
  const [commReview, setCommReview] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    Promise.all([
      api.get("/dashboard/kpis"),
      api.get("/dashboard/submissions-heatmap"),
      api.get("/dashboard/pipeline"),
      api.get("/sales/commissions/summary").catch(() => ({ data: null })),
    ])
      .then(([k, h, p, c]) => { setKpis(k.data); setHeatmap(h.data); setPipeline(p.data); setCommReview(c.data); })
      .finally(() => setLoading(false));
  }, []);

  if (loading || !kpis) {
    return (
      <div className="space-y-6">
        <div className="h-7 w-48 bg-slate-800/50 rounded animate-pulse" />
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          {[1,2,3,4].map((i) => <div key={i} className="h-32 rounded-xl bg-slate-900/40 border border-slate-800 animate-pulse" />)}
        </div>
      </div>
    );
  }

  const statusData = Object.entries(kpis.by_status || {}).map(([name, value]) => ({ name, value }));
  const revenueData = kpis.revenue_by_month || [];

  return (
    <div className="space-y-7 animate-slide-up" data-testid="dashboard-page">
      <div className="flex items-end justify-between">
        <div>
          <div className="text-xs uppercase tracking-[0.22em] text-slate-500 font-bold mb-2">{t("dashboard")}</div>
          <h1 className="font-display text-4xl font-semibold tracking-tight">Operations Control Room</h1>
          <p className="text-sm text-slate-400 mt-1.5">Real-time view of revenue, jobs, and ADAS workload across all partners.</p>
        </div>
        <div className="text-xs font-mono text-slate-500">
          {new Date().toLocaleDateString(undefined, { weekday: "short", month: "short", day: "numeric", year: "numeric" })}
        </div>
      </div>

      {/* Iter 47 Phase 4: authoritative ledger cards with drill-down */}
      <LedgerKPICards />

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 opacity-70">
        <KPI label="Open Jobs" value={pipeline?.operational?.open_jobs ?? kpis.active_jobs} sub={`${kpis.total_jobs} total in pipeline`} icon={Wrench} accent="blue" />
        <KPI label="Pending Approvals" value={kpis.pending_approvals} sub="body shops waiting" icon={ShieldAlert} accent="amber" />
        <KPI label="Completed" value={kpis.completed_jobs} sub={`Avg ticket $${(kpis.avg_ticket || 0).toFixed(0)}`} icon={Sparkles} accent="cyan" />
        <KPI label="Total Jobs" value={kpis.total_jobs} sub="all time" icon={TrendingUp} accent="slate" />
      </div>

      {/* Commissions review alert — only when there are flagged commissions */}
      {commReview && commReview.needs_review_count > 0 && (
        <Link
          to="/sales/commissions?status=needs_review"
          className="block rounded-xl border border-rose-500/40 bg-rose-500/10 hover:bg-rose-500/15 p-4 transition-colors group"
          data-testid="commissions-review-widget"
        >
          <div className="flex items-center justify-between flex-wrap gap-3">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-rose-500/20 border border-rose-500/40 flex items-center justify-center text-lg">⚠️</div>
              <div>
                <div className="text-[10px] uppercase tracking-[0.18em] font-bold text-rose-300">Comissões precisando revisão</div>
                <div className="font-display text-xl font-semibold text-slate-100 mt-0.5">
                  {commReview.needs_review_count} comissão{commReview.needs_review_count > 1 ? "s" : ""} pendente{commReview.needs_review_count > 1 ? "s" : ""} de decisão
                </div>
                <div className="text-xs text-slate-400 mt-0.5">
                  Total afetado: <span className="font-mono text-rose-200 font-semibold">${commReview.needs_review_amount.toLocaleString()}</span> · Invoice foi cancelada após comissão liberada
                </div>
              </div>
            </div>
            <div className="flex items-center gap-2 text-xs text-rose-200 font-bold uppercase tracking-wider group-hover:text-rose-100">
              Revisar agora <ArrowRight className="w-4 h-4 group-hover:translate-x-0.5 transition-transform" />
            </div>
          </div>
        </Link>
      )}

      {/* 13-state pipeline strip */}
      {pipeline && (
        <div className="rounded-xl border border-slate-800 bg-[#0B1120] p-5" data-testid="pipeline-strip">
          <div className="flex items-end justify-between mb-4 flex-wrap gap-2">
            <div>
              <div className="text-[10px] uppercase tracking-[0.22em] font-bold text-slate-500">Operational Pipeline</div>
              <h3 className="font-display text-xl font-medium mt-1">Jobs by Stage · 13-step workflow</h3>
            </div>
            <div className="text-xs text-slate-500 font-mono">{pipeline.total_jobs} total · live</div>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 xl:grid-cols-12 gap-2">
            {PIPELINE_COLUMNS.map((col) => {
              const found = (pipeline.pipeline || []).find((p) => p.status === col.key);
              const count = found ? found.count : 0;
              const accent = ACCENT_BG[col.accent] || ACCENT_BG.slate;
              return (
                <div
                  key={col.key}
                  data-testid={`pipeline-bucket-${col.key}`}
                  className={`rounded-lg border p-2.5 ${accent} ${count === 0 ? "opacity-50" : ""}`}
                >
                  <div className="text-[9px] uppercase tracking-wider font-bold opacity-80 leading-tight min-h-[24px]">
                    {col.label}
                  </div>
                  <div className="font-display text-2xl font-semibold mt-1">{count}</div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      <DashboardAlerts />

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <div className="lg:col-span-2 rounded-xl border border-slate-800 bg-[#0B1120] p-6">
          <div className="flex items-end justify-between mb-4">
            <div>
              <div className="text-[10px] uppercase tracking-[0.22em] font-bold text-slate-500">Revenue · Last 6 months</div>
              <h3 className="font-display text-xl font-medium mt-1">Insurance Billing Trajectory</h3>
            </div>
            <div className="text-xs text-emerald-400 font-mono flex items-center gap-1.5">
              <TrendingUp className="w-3.5 h-3.5" /> +18.4%
            </div>
          </div>
          <ResponsiveContainer width="100%" height={260}>
            <AreaChart data={revenueData}>
              <defs>
                <linearGradient id="rev" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor="#2563EB" stopOpacity={0.5} />
                  <stop offset="100%" stopColor="#2563EB" stopOpacity={0} />
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" stroke="#1e293b" vertical={false} />
              <XAxis dataKey="month" stroke="#64748b" fontSize={11} tickLine={false} />
              <YAxis stroke="#64748b" fontSize={11} tickLine={false} axisLine={false} tickFormatter={(v) => `$${v / 1000}k`} />
              <Tooltip contentStyle={{ background: "#0B1120", border: "1px solid #1e293b", borderRadius: 8, color: "#fff" }} />
              <Area type="monotone" dataKey="revenue" stroke="#3B82F6" strokeWidth={2} fill="url(#rev)" />
            </AreaChart>
          </ResponsiveContainer>
        </div>

        <div className="rounded-xl border border-slate-800 bg-[#0B1120] p-6">
          <div className="text-[10px] uppercase tracking-[0.22em] font-bold text-slate-500">Jobs by Status</div>
          <h3 className="font-display text-xl font-medium mt-1 mb-3">Pipeline</h3>
          <ResponsiveContainer width="100%" height={220}>
            <PieChart>
              <Pie data={statusData} dataKey="value" nameKey="name" innerRadius={50} outerRadius={80} paddingAngle={3}>
                {statusData.map((_, i) => <Cell key={i} fill={PIE_COLORS[i % PIE_COLORS.length]} />)}
              </Pie>
              <Tooltip contentStyle={{ background: "#0B1120", border: "1px solid #1e293b", borderRadius: 8, color: "#fff" }} />
            </PieChart>
          </ResponsiveContainer>
          <div className="space-y-1.5 mt-2">
            {statusData.map((s, i) => (
              <div key={s.name} className="flex items-center justify-between text-xs">
                <div className="flex items-center gap-2">
                  <div className="w-2 h-2 rounded-full" style={{ background: PIE_COLORS[i % PIE_COLORS.length] }} />
                  <span className="text-slate-400 capitalize">{s.name.replace("_", " ")}</span>
                </div>
                <span className="font-mono text-slate-300">{s.value}</span>
              </div>
            ))}
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <div className="rounded-xl border border-slate-800 bg-[#0B1120] p-6">
          <div className="text-[10px] uppercase tracking-[0.22em] font-bold text-slate-500">Top Insurers</div>
          <h3 className="font-display text-xl font-medium mt-1 mb-4">Revenue Partners</h3>
          <div className="space-y-2.5">
            {(kpis.top_insurers || []).map((ins, i) => (
              <div key={ins.name} className="flex items-center justify-between p-3 rounded-lg bg-slate-900/40 border border-slate-800/50">
                <div className="flex items-center gap-3">
                  <div className="font-mono text-xs text-slate-500 w-6">#{i + 1}</div>
                  <ShieldAlert className="w-4 h-4 text-blue-400" />
                  <span className="text-sm">{ins.name}</span>
                </div>
                <div className="flex items-center gap-4">
                  <span className="text-xs text-slate-500 font-mono">{ins.jobs} jobs</span>
                  <span className="font-mono text-emerald-400">${ins.revenue.toLocaleString()}</span>
                </div>
              </div>
            ))}
            {(!kpis.top_insurers || kpis.top_insurers.length === 0) && (
              <div className="text-sm text-slate-500 text-center py-8">No data yet</div>
            )}
          </div>
        </div>

        {heatmap && <Heatmap matrix={heatmap.matrix} days={heatmap.days} total={heatmap.total_jobs} by_shop={heatmap.by_shop} />}
      </div>
    </div>
  );
}
